import type React from "react";

import { cn } from "@/lib/utils";

import type { IconoirIconProps, IconSize, IconVariant } from "./types";
import { SIZE_CLASSES, STROKE_WEIGHTS } from "./types";

type IconoirComponent = React.FC<React.SVGProps<SVGSVGElement>>;

/**
 * Factory function to create a sized icon component from an Iconoir icon.
 *
 * @param IconoirIcon - The base Iconoir icon component
 * @param size - Fixed pixel size (12, 16, 20, 24, 32, 48, 64)
 * @param _variant - Visual variant (Line, Solid, Color) - used for naming only
 * @returns A React component with fixed size and stroke weight
 */
export function createIcon(
  IconoirIcon: IconoirComponent,
  size: IconSize,
  _variant: IconVariant
): React.FC<IconoirIconProps> {
  const strokeWeight = STROKE_WEIGHTS[size];
  const sizeClass = SIZE_CLASSES[size];

  const WrappedIcon: React.FC<IconoirIconProps> = ({ className, ...props }) => (
    <IconoirIcon
      width={size}
      height={size}
      strokeWidth={strokeWeight}
      className={cn(sizeClass, className)}
      {...props}
    />
  );

  return WrappedIcon;
}
